import React from 'react';
import { Document, Page as RPDFPage, View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  ADJUSTMENT_KIND_LABEL, FEE_INVOICE_STATUS_META,
  type FeeAdjustmentKind, type FeeVoucherPrint, type FeeVoucherPrintSet,
} from '../../../types/fees';
import {
  DataTable, Footer, LogoMark, Pill, RunningHeader, Signatures, Watermark,
  pdfColors, pdfFontFamily, pdfLetter, pdfPageFrame, pdfSpacing, fmtClassName, fmtDate, fmtMoney,
  type DataTableColumn, type PdfTone, type SchoolBranding,
} from '../../../utils/pdf';

interface Props {
  set: FeeVoucherPrintSet;
  branding: SchoolBranding;
  generatedAt: string;
}

/** The screen's status tones, in the PDF kit's names. */
const TONE: Record<string, PdfTone> = { success: 'success', warning: 'warning', error: 'danger', info: 'info', neutral: 'neutral' };

const AMBER = { soft: '#fffbeb', line: '#fcd34d', dash: '#fde68a', ink: '#78350f', mid: '#92400e', plus: '#b45309' };

const label = {
  fontSize: 6.8, fontWeight: 600, color: pdfColors.textMuted, textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong,
} as const;

const styles = StyleSheet.create({
  page: {
    paddingTop: pdfPageFrame.contentTop,
    paddingBottom: pdfPageFrame.contentBottom,
    paddingHorizontal: pdfSpacing.pageX,
    fontFamily: pdfFontFamily.sans,
    fontSize: 9,
    color: pdfColors.textPrimary,
    backgroundColor: pdfColors.surface,
  },
  // Page 1 has no running header, so the voucher's header pulls up into that band.
  header: {
    marginTop: pdfPageFrame.mastheadTop - pdfPageFrame.contentTop,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: 10, marginBottom: 14, borderBottomWidth: 1.5, borderBottomColor: pdfColors.textPrimary, borderBottomStyle: 'solid',
  },
  brand: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
  brandText: { marginLeft: 10, flexShrink: 1 },
  school: { fontSize: 14, fontWeight: 700, letterSpacing: -0.1 },
  schoolMeta: { fontSize: 8, color: pdfColors.textSecondary, marginTop: 1.5 },
  doc: { alignItems: 'flex-end', marginLeft: pdfSpacing.md },
  docLabel: label,
  number: { fontFamily: pdfFontFamily.mono, fontSize: 13, fontWeight: 700, marginTop: 2 },
  chips: { flexDirection: 'row', marginTop: 5 },
  chipGap: { marginLeft: 4 },
  meta: {
    flexDirection: 'row', flexWrap: 'wrap', marginBottom: 12, paddingVertical: 9, paddingHorizontal: 12,
    borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 6, backgroundColor: pdfColors.surfaceSoft,
  },
  metaItem: { width: '50%', paddingVertical: 3 },
  metaLabel: label,
  metaValue: { fontSize: 9.5, fontWeight: 600, marginTop: 1.5 },
  metaValueSmall: { fontSize: 9, fontWeight: 500, marginTop: 1.5 },
  cards: { flexDirection: 'row', marginBottom: 12 },
  card: {
    flex: 1, paddingVertical: 9, paddingHorizontal: 12,
    borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 6,
  },
  cardGap: { marginLeft: 12 },
  cardLabel: { ...label, marginBottom: 4 },
  cardName: { fontSize: 11, fontWeight: 700 },
  cardSub: { fontSize: 8.5, color: pdfColors.textSecondary, marginTop: 2 },
  ref: { fontFamily: pdfFontFamily.mono, fontSize: 8, color: pdfColors.textPrimary },
  strongInk: { color: pdfColors.textPrimary, fontWeight: 600 },
  faint: { color: pdfColors.textFaint },
  muted: { color: pdfColors.textMuted },
  item: { fontWeight: 500 },
  net: { fontWeight: 700 },
  plus: { fontWeight: 700, color: AMBER.plus },
  minus: { fontWeight: 700, color: pdfColors.success },
  arrears: {
    marginTop: 4, marginBottom: 12, paddingVertical: 9, paddingHorizontal: 12,
    borderWidth: 0.6, borderColor: AMBER.line, borderStyle: 'solid', borderRadius: 6, backgroundColor: AMBER.soft,
  },
  arrearsTitle: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
  arrearsHeading: { ...label, color: AMBER.mid },
  arrearsCount: { fontSize: 7.5, color: '#a16207' },
  arrearsHead: { flexDirection: 'row', paddingVertical: 3, borderBottomWidth: 0.6, borderBottomColor: AMBER.line, borderBottomStyle: 'solid' },
  arrearsTh: { fontSize: 6.5, fontWeight: 700, color: AMBER.ink, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel },
  arrearsRow: { flexDirection: 'row', paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: AMBER.dash, borderBottomStyle: 'dashed' },
  arrearsTotal: { flexDirection: 'row', paddingTop: 5, borderTopWidth: 0.6, borderTopColor: AMBER.line, borderTopStyle: 'solid' },
  arrearsCell: { fontSize: 8.5 },
  arrearsFigure: { width: 100, textAlign: 'right', fontSize: 8.5, fontWeight: 600 },
  arrearsStrong: { fontSize: 8.5, fontWeight: 700, color: AMBER.ink },
  totals: { alignSelf: 'flex-end', width: 236, borderWidth: 0.6, borderColor: pdfColors.divider, borderStyle: 'solid', borderRadius: 6 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5, paddingHorizontal: 10 },
  totalRule: { borderTopWidth: 0.5, borderTopColor: pdfColors.rule, borderTopStyle: 'solid' },
  totalLabel: { fontSize: 9, color: pdfColors.textSecondary },
  totalValue: { fontSize: 9 },
  signatures: { marginTop: 30 },
});

/** The last row of the totals box — what is owed, or that nothing is. */
const FINAL: Record<'due' | 'settled' | 'void' | 'grand', { bg: string; ink: string }> = {
  due: { bg: pdfColors.warningSoft, ink: pdfColors.warning },
  settled: { bg: pdfColors.successSoft, ink: pdfColors.success },
  void: { bg: pdfColors.neutralSoft, ink: pdfColors.neutralText },
  grand: { bg: pdfColors.warningSoft, ink: pdfColors.warning },
};

type Row =
  | { type: 'line'; name: string; gross: number; concession: number; waived: number; net: number }
  | { type: 'adjustment'; kind: FeeAdjustmentKind; amount: number };

const Voucher: React.FC<{ v: FeeVoucherPrint; set: FeeVoucherPrintSet; branding: SchoolBranding; generatedAt: string }> = ({ v, set, branding, generatedAt }) => {
  const money = (n: number) => fmtMoney(n, set.currency);
  const less = (n: number) => (n > 0 ? <Text style={styles.muted}>-{money(n)}</Text> : <Text style={styles.faint}>—</Text>);
  const signed = (n: number) => `${n >= 0 ? '+' : '-'}${money(Math.abs(n))}`;
  const t = v.totals;
  const status = FEE_INVOICE_STATUS_META[v.status];
  const cancelled = v.status === 'cancelled';
  const withArrears = v.arrears.length > 0;

  const rows: Row[] = [
    ...v.lines.map(l => ({ type: 'line' as const, name: l.categoryName, gross: l.gross, concession: l.concession, waived: l.waived, net: l.net })),
    ...v.adjustments.map(a => ({ type: 'adjustment' as const, kind: a.kind, amount: a.amount })),
  ];
  const dash = <Text style={styles.faint}>—</Text>;
  const columns: Array<DataTableColumn<Row>> = [
    { key: 'n', header: '#', width: 22, align: 'center', render: (_r, i) => <Text style={styles.faint}>{i + 1}</Text> },
    {
      key: 'item', header: 'Line item', flex: 1,
      render: r => (r.type === 'line'
        ? <Text style={styles.item}>{r.name}</Text>
        : <View style={{ alignSelf: 'flex-start' }}><Pill dense label={ADJUSTMENT_KIND_LABEL[r.kind] ?? 'Other'} tone={r.kind === 'late_fee' ? 'warning' : 'neutral'} /></View>),
    },
    { key: 'gross', header: 'Gross', width: 78, align: 'right', render: r => (r.type === 'line' ? money(r.gross) : dash) },
    { key: 'concession', header: 'Concession', width: 78, align: 'right', render: r => (r.type === 'line' ? less(r.concession) : dash) },
    { key: 'waiver', header: 'Waiver', width: 70, align: 'right', render: r => (r.type === 'line' ? less(r.waived) : dash) },
    {
      key: 'net', header: 'Net', width: 84, align: 'right',
      render: r => (r.type === 'line'
        ? <Text style={styles.net}>{money(r.net)}</Text>
        : <Text style={r.amount >= 0 ? styles.plus : styles.minus}>{signed(r.amount)}</Text>),
    },
  ];

  const totalRow = (text: string, value: string, first = false) => (
    <View key={text} style={first ? styles.totalRow : [styles.totalRow, styles.totalRule]}>
      <Text style={styles.totalLabel}>{text}</Text>
      <Text style={styles.totalValue}>{value}</Text>
    </View>
  );
  const finalRow = (tone: keyof typeof FINAL, text: string, value: string, size = 10) => (
    <View style={[styles.totalRow, { backgroundColor: FINAL[tone].bg, paddingVertical: 7 }]}>
      <Text style={{ fontSize: size, fontWeight: 700, color: FINAL[tone].ink }}>{text}</Text>
      <Text style={{ fontSize: size, fontWeight: 700, color: FINAL[tone].ink }}>{value}</Text>
    </View>
  );

  return (
    <RPDFPage size="A4" style={styles.page} wrap>
      {/* A voucher that runs on keeps its number and payer on every later page — in a pack especially. */}
      <RunningHeader branding={branding} meta={{ title: 'Fee Voucher', subtitle: `${v.invoiceNumber}  ·  ${v.payer.name}` }} />
      {cancelled && <Watermark text="CANCELLED" />}

      <View style={styles.header} wrap={false}>
        <View style={styles.brand}>
          <LogoMark branding={branding} size={46} />
          <View style={styles.brandText}>
            <Text style={styles.school}>{set.school.name}</Text>
            {set.school.address && <Text style={styles.schoolMeta}>{set.school.address}</Text>}
            {set.school.phone && <Text style={styles.schoolMeta}>{set.school.phone}</Text>}
          </View>
        </View>
        <View style={styles.doc}>
          <Text style={styles.docLabel}>Fee voucher no</Text>
          <Text style={styles.number}>{v.invoiceNumber}</Text>
          <View style={styles.chips}>
            <Pill dense label={status.label} tone={TONE[status.tone]} />
            {v.overdue && <View style={styles.chipGap}><Pill dense label="Overdue" tone="danger" /></View>}
          </View>
        </View>
      </View>

      <View style={styles.meta} wrap={false}>
        <View style={styles.metaItem}><Text style={styles.metaLabel}>Period</Text><Text style={styles.metaValue}>{v.periodLabel || '—'}</Text></View>
        <View style={styles.metaItem}><Text style={styles.metaLabel}>Due date</Text><Text style={styles.metaValue}>{fmtDate(v.dueDate)}</Text></View>
        <View style={styles.metaItem}><Text style={styles.metaLabel}>Issued on</Text><Text style={styles.metaValueSmall}>{fmtDate(v.issuedAt)}</Text></View>
        {v.sessionName && <View style={styles.metaItem}><Text style={styles.metaLabel}>Session</Text><Text style={styles.metaValueSmall}>{v.sessionName}</Text></View>}
      </View>

      <View style={styles.cards} wrap={false}>
        <View style={styles.card}>
          <Text style={styles.cardLabel}>Billed to</Text>
          <Text style={styles.cardName}>{v.payer.name}</Text>
          {v.payer.fatherName && <Text style={styles.cardSub}>s/o {v.payer.fatherName}</Text>}
          {v.payer.applicationNumber && <Text style={styles.cardSub}>Application No. <Text style={styles.ref}>{v.payer.applicationNumber}</Text></Text>}
          {v.payer.grNumber && <Text style={styles.cardSub}>GR Number <Text style={styles.ref}>{v.payer.grNumber}</Text></Text>}
          {v.payer.address && <Text style={styles.cardSub}>{v.payer.address}</Text>}
        </View>
        <View style={[styles.card, styles.cardGap]}>
          <Text style={styles.cardLabel}>Cohort</Text>
          <Text style={styles.cardName}>
            {fmtClassName(v.cohort.className) || '—'}
            {v.cohort.sectionName && <Text style={{ color: pdfColors.textMuted, fontWeight: 500 }}>{` – ${v.cohort.sectionName}`}</Text>}
          </Text>
          {v.cohort.rollNumber != null && <Text style={styles.cardSub}>Roll <Text style={styles.strongInk}>{String(v.cohort.rollNumber)}</Text></Text>}
          {v.cohort.planName && <Text style={styles.cardSub}>Plan: {v.cohort.planName}</Text>}
        </View>
      </View>

      <DataTable
        columns={columns}
        rows={rows}
        zebra
        rowStyle={r => (r.type === 'adjustment' ? { backgroundColor: r.kind === 'late_fee' ? AMBER.soft : pdfColors.surfaceSoft } : undefined)}
      />

      {withArrears && (
        <View style={[styles.arrears, { marginTop: 12 }]} wrap={false}>
          <View style={styles.arrearsTitle}>
            <Text style={styles.arrearsHeading}>Arrears · prior unpaid challans</Text>
            <Text style={styles.arrearsCount}>{v.arrears.length} pending</Text>
          </View>
          <View style={styles.arrearsHead}>
            <Text style={[styles.arrearsTh, { flex: 1 }]}>Period</Text>
            <Text style={[styles.arrearsTh, { width: 110 }]}>Challan no</Text>
            <Text style={[styles.arrearsTh, { width: 100, textAlign: 'right' }]}>Outstanding</Text>
          </View>
          {v.arrears.map(a => (
            <View key={a.invoiceNumber} style={styles.arrearsRow}>
              <Text style={[styles.arrearsCell, { flex: 1 }]}>{a.periodLabel || '—'}</Text>
              <Text style={[styles.ref, { width: 110 }]}>{a.invoiceNumber}</Text>
              <Text style={styles.arrearsFigure}>{money(a.outstanding)}</Text>
            </View>
          ))}
          <View style={styles.arrearsTotal}>
            <Text style={[styles.arrearsStrong, { flex: 1 }]}>Total arrears</Text>
            <Text style={[styles.arrearsStrong, { width: 100, textAlign: 'right' }]}>{money(t.arrears)}</Text>
          </View>
        </View>
      )}

      <View style={[styles.totals, { marginTop: withArrears ? 0 : 12 }]} wrap={false}>
        {totalRow('Gross', money(t.gross), true)}
        {t.concessions > 0 && totalRow('Concessions', `-${money(t.concessions)}`)}
        {t.waivers > 0 && totalRow('Waivers', `-${money(t.waivers)}`)}
        {t.lateFees !== 0 && totalRow('Late fees', signed(t.lateFees))}
        {t.otherAdjustments !== 0 && totalRow('Adjustments', signed(t.otherAdjustments))}
        <View style={[styles.totalRow, styles.totalRule, { backgroundColor: pdfColors.surfaceSoft }]}>
          <Text style={[styles.totalLabel, { fontWeight: 700, color: pdfColors.textPrimary }]}>Net payable</Text>
          <Text style={[styles.totalValue, { fontWeight: 700 }]}>{money(t.net)}</Text>
        </View>
        {t.paid > 0 && totalRow('Paid', money(t.paid))}
        {cancelled
          ? finalRow('void', 'Cancelled — not payable', money(0))
          : withArrears
            ? (
              <>
                {totalRow("This challan's balance", money(t.outstanding))}
                <View style={[styles.totalRow, styles.totalRule]}>
                  <Text style={styles.totalLabel}>Arrears ({v.arrears.length} prior)</Text>
                  <Text style={[styles.totalValue, { color: AMBER.plus }]}>+{money(t.arrears)}</Text>
                </View>
                {finalRow('grand', 'Grand total payable', money(t.grandTotal), 10.5)}
              </>
            )
            : t.outstanding > 0
              ? finalRow('due', 'Balance due', money(t.outstanding))
              : finalRow('settled', v.status === 'waived' ? 'Waived — nothing to pay' : 'Fully paid', money(0))}
      </View>

      <View style={styles.signatures}>
        <Signatures slots={['Received by', 'Accountant']} />
      </View>

      <Footer meta={{ title: 'Fee Voucher', generatedAt, signed: true }} />
    </RPDFPage>
  );
};

/**
 * Fee vouchers (challans) — one page each, in the order given (the server
 * sorts a pack by class, section and roll). The school's header, the period
 * and due date, who is billed, the lines with any adjustments, earlier unpaid
 * challans while this one is open, and the totals down to what is payable.
 * A cancelled voucher is marked so and says it is not payable.
 */
const FeeVoucherDocument: React.FC<Props> = ({ set, branding, generatedAt }) => (
  <Document
    title={set.vouchers.length === 1 ? `Fee Voucher · ${set.vouchers[0].invoiceNumber}` : `Fee Vouchers · ${set.vouchers.length} challans`}
    author={branding.name}
  >
    {set.vouchers.map(v => <Voucher key={v.invoiceNumber} v={v} set={set} branding={branding} generatedAt={generatedAt} />)}
  </Document>
);

export default FeeVoucherDocument;
